/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import mbpLogger from 'mbp-logger';
import { object, func, bool } from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { useUIDSeed } from 'react-uid';
import { useHistory } from 'react-router';
import { useMutation } from 'react-query';
import { withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';

import { useForm, FormProvider } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { schema } from './Schema';

import { withStyles } from '@material-ui/core/styles';
import { Button, Grid } from '@material-ui/core';

import { buildRecipientPayload } from './Helpers';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { useAuth } from '../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import { getAccessTokenSafely } from '../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { getUserProfile } from '../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { focusOnElement } from '../../../pages/Account/components/AddressBook/RecipientBook/Common/Helpers/Helpers';
import { getIsAuthenticatedStatus } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { processAddNewRecipient } from '../../../../apis/account-apis/RecipientBook-apis/processAddNewRecipient';
import { trackPage } from '../../../../app/helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../helpers/tracking/hocs/withPageView';

import CelebrationsGuestForm from './CelebrationsGuestForm';
import CelebrationsRemindersHeader from './CelebrationsRemindersHeader';
import CelebrationsRecipientFormCard from './CelebrationsRecipientFormCard';

const styles = () => ({
    mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 'calc(110vh - 236px)',
        zIndex: 2,
        alignItems: 'center',
        backgroundColor: '#FFF8F0',
        position: 'relative',
    },
    innerContainer: {
        marginTop: '37px',
        maxWidth: '1400px',
        margin: '0 auto',
        '@media (min-width:767px)': {
            marginTop: '88px',
        },
    },
    welcomeContainer: {
        marginTop: '16px',
    },
    loginText: {
        fontFamily: 'Lato, sans-serif',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '21px',
    },
    staticLink: {
        fontWeight: 700,
        fontFamily: 'Lato, sans-serif',
        textTransform: 'none',
        verticalAlign: 'top',
        textDecoration: 'underline',
        cursor: 'pointer',
        outline: 'none',
        textUnderlineOffset: '2px',
    },
    formContainer: {
        width: '100%',
        padding: '0px 16px',
        '@media (min-width:767px)': {
            maxWidth: '700px',
        },
    },
    btnContainer: {
        textAlign: 'center',
        margin: '30px 0 50px 0',
    },
    remindersBtn: {
        color: '#FFF',
        height: '56px',
        backgroundColor: '#329A77 !important',
        border: '3px solid #329A77 !important',
        borderRadius: '3px !important',
        textTransform: 'none',
        textAlign: 'center',
        lineHeight: '26px',
        width: '100% !important',
        margin: '0 !important',
        float: 'unset !important',
        fontFamily: 'Lato, sans-serif !important',
        '& span': {
            fontSize: '18px !important',
        },
        '&:hover, &:focus, &:active': {
            backgroundColor: '#329A77 !important',
        },
        '@media (min-width:767px)': {
            maxWidth: '380px',
        },
    },
    fieldWrapper: {
        marginBottom: '12px',
        borderRadius: '3px',
        backgroundColor: '#fff',
        '& .MuiInputBase-root': {
            height: '56px',
        },
    },
    contactSectionContainer: {
        marginBottom: '20px',
        '@media (min-width:767px)': {
            marginBottom: '32px',
        },
    },
    contactContainer: {
        marginBottom: '20px',
    },
    addContactContainer: {
        textAlign: 'center',
        marginTop: '32px',
        marginBottom: '48px',
    },
    underlay: {
        // left: 0,
        zIndex: '-1',
        width: '100vw',
        height: '100%',
        position: 'absolute',
        background: '#FFF8F0',
    },
});

const CelebrationsRemindersPage = ({ classes, callTrackEvent, isAuthenticated }) => {
    const seed = useUIDSeed();
    const history = useHistory();
    const { loginByRedirect } = useAuth();
    const { ContactId = '', email: userProfileEmail } = useSelector(getUserProfile);
    const brandId = useSelector(getBrand).id;
    const maxContactCount = 2;
    let defaultCustomer = { firstName: '', lastName: '', email: '' };
    let defaultRecipients = [{ rcpFirstName: '', rcpLastName: '', occasions: [ { occasion: '', date: '' }, { occasion: '', date: '' }]}];
    
    const processAddNewRecipientMutation = useMutation({
        mutationFn: processAddNewRecipient,
        onSuccess: () => {},
        onError: (err) => {
            mbpLogger.logError({
                function: 'processAddNewRecipientMutation',
                appName: process.env.npm_package_name,
                module: 'CelebrationsRemindersPage.js',
                message: `Error While Adding a Celebrations Recipient for Contact ID: ${ContactId}`,
                err,
            });
        },
    });

    const handleCreateRecipient = async (recipients) => {
        const jwtToken = await getAccessTokenSafely();

        await processAddNewRecipientMutation.mutateAsync({ ContactId, data: recipients[0], jwtToken });
        if (Object.keys(recipients[1] || {})?.length > 0) {
            await processAddNewRecipientMutation.mutateAsync({ ContactId, data: recipients[1], jwtToken });
        }

        history.push(`/account/address-book#guest-started`);
        sessionStorage.removeItem('persist:guestRecipients');
    };

    // Read data from local storage after sign up & fire API calls
    let existingRecipients = sessionStorage.getItem("persist:guestRecipients");
    if (existingRecipients && typeof existingRecipients === 'string') {
        existingRecipients = JSON.parse(existingRecipients);
        defaultCustomer = existingRecipients['customer'];
        defaultRecipients = existingRecipients['recipients'];

        if (isAuthenticated && !processAddNewRecipientMutation.isSuccess) {
            const formattedRecipients = buildRecipientPayload({
                recipients: defaultRecipients,
                customer: defaultCustomer,
                brandId,
                email: userProfileEmail
            });
            handleCreateRecipient(formattedRecipients);
        }
    };

    // Redirect user to address book if logged in user is trying to visit the page
    if (isAuthenticated) {
        history.push(`/account/address-book`);
    };

    /* React Hook Form */
    const methods = useForm({
        mode: 'all',
        resolver: joiResolver(schema),
        defaultValues: {
            customer: defaultCustomer,
            recipients: defaultRecipients,
        },
    });
    const { formState: { errors }, watch, setValue, handleSubmit, setError  } = methods;
    const formState = watch();

    useEffect(() => {
        if (errors?.recipients) {
            focusOnElement({ selectorInputToSearch: '.Mui-error input' });
        }
    }, [errors]);

    const addRecipient = () => {
        if (formState.recipients?.length < maxContactCount) {
            const newRecipient = {
                rcpFirstName: '',
                rcpLastName: '',
                occasions: [
                    { occasion: '', date: '' },
                    { occasion: '', date: '' },
                ],
            };
            setValue('recipients', [...formState.recipients, newRecipient]);
        }
        return;
    };

    const handleSignIn = async (data) => {
        callTrackEvent({
            eventCategory: 'Sign In',
            eventAction: 'Login',
            eventLabel: 'Logon_From_GuestReminders|<<pageType>>',
        });

        await loginByRedirect({
            redirect_uri: `${window.location.origin}/auth/callback`,
        }, {
            routeBack: '/account/address-book',
        });
    };

    const handleSignUp = async (data) => {
        callTrackEvent({
            eventCategory: 'Sign In',
            eventAction: 'Sign Up',
            eventLabel: 'Logon_From_GuestReminders|<<pageType>>',
        });

        await loginByRedirect({
            screen_hint: 'signup',
            'ext-reminders-flow': 'yes',
            'ext-first-name': data.customer?.firstName || '',
            'ext-last-name': data.customer?.lastName || '',
            login_hint: data.customer?.email || '',
        });
    };

    const onSubmit = async (data) => {
        //eslint-disable-next-line no-useless-escape
        const emailPattern = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        const email = data.customer?.email;

        if (email && !emailPattern.test(email)) {
            setError("customer.email", {
                type: "manual",
                message: "Invalid email format",
            });
            return;
        }

        const contactCount = data.recipients?.length;
        const occasionCount = data.recipients?.reduce((total, recipient) => {
            const validOccasionsCount = recipient.occasions.filter(reminder => reminder.occasion?.trim() !== '')?.length;
            return total + validOccasionsCount;
        }, 0);

        callTrackEvent({
            eventCategory: "Guest-Started",
            eventAction: "Continue CTA",
            eventLabel: `Contacts: ${contactCount}, Reminders: ${occasionCount}`,
            eventName: 'feature_interaction',
            feature_category: "Guest-Started",
            feature_element: "Continue CTA",
            feature_detail: `Contacts: ${contactCount}, Reminders: ${occasionCount}`,
        });
        sessionStorage.setItem("persist:guestRecipients", JSON.stringify(data));
        handleSignUp(data);
    };

    return (
        <div className={classes.mainContainer}>
            <Grid container direction="column" className={classes.innerContainer} alignItems="center">
                {!isAuthenticated && <CelebrationsRemindersHeader handleSignIn={handleSignIn} />}
                <Grid item container className={classes.formContainer} direction="column" alignItems="center">
                    <FormProvider {...methods}>
                        <form autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={3} className={classes.contactContainer}>
                                {formState.recipients?.map((recipient, index) => (
                                    <Grid item xs={12} key={seed(index)} className={errors?.recipients?.[index] ? `helper-text-error-${index}` : '' }>
                                        <CelebrationsRecipientFormCard 
                                            recipient={recipient} 
                                            recipientIndex={index}
                                            />
                                    </Grid>
                                ))}
                            </Grid>
                            {formState.recipients?.length < maxContactCount && (
                                <Grid item className={classes.addContactContainer}>
                                    <span
                                        role="button"
                                        tabIndex={-1}
                                        onClick={addRecipient}
                                        onKeyDown={addRecipient}
                                        className={classes.staticLink}
                                    >
                                        Add Another Loved One
                                    </span>{' '}
                                </Grid>
                            )}
                            <CelebrationsGuestForm />
                            <Grid item xs={12} className={classes.btnContainer}>
                                <Button className={classes.remindersBtn} type="submit" fullWidth>Set Reminders</Button>
                            </Grid>
                        </form>
                    </FormProvider>
                </Grid>
            </Grid>
            <div className={classes.underlay} />
        </div>
    );
};

CelebrationsRemindersPage.propTypes = {
    callTrackEvent: func.isRequired,
    classes: object,
    isAuthenticated: bool.isRequired,
};

CelebrationsRemindersPage.defaultProps = {
    classes: {},
};

const mapStateToProps = (state) => ({
    isAuthenticated: getIsAuthenticatedStatus(state),
});

const mapDispatchToProps = (dispatch) => ({
    callTrackEvent: bindActionCreators(trackEvent, dispatch),
});

const enhance = compose(
    trackPage({
        type: 'guestreminders',
        name: 'GuestReminders',
        accountPage: true,
    }),
    withPageView({
        pageViewSuccessTest: () => true,
    }),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    withStyles(styles),
)

export default enhance(CelebrationsRemindersPage);
